import type { AllSchemaKeys, ApiVersions, OpalApiComponent } from '..'
import { findResourceOfType } from './resourceObject'

type ResourceLike = { id: string; type: string }

export type IncludedLookup<TVersion extends ApiVersions> = <
  TResourceType extends AllSchemaKeys,
>(
  resourceType: TResourceType,
  resourceId: string | undefined
) => OpalApiComponent<TVersion, TResourceType> | undefined

/**
 * Indexes a document’s `included` once, so each lookup is constant-time rather
 * than an `included.find(findResourceOfType(…))` scan. Selectors that resolve
 * several relationships per resource should build one lookup per document.
 * As with `find`, the first of any duplicated resources wins.
 */
export const createIncludedLookup = <TVersion extends ApiVersions>(
  included: ReadonlyArray<ResourceLike> | undefined,
  version: TVersion
): IncludedLookup<TVersion> => {
  const resourcesByKey = new Map<string, ResourceLike>()
  included?.forEach((resource) => {
    const key = `${resource.type}:${resource.id}`
    if (!resourcesByKey.has(key)) resourcesByKey.set(key, resource)
  })

  return (resourceType, resourceId) => {
    const resource = resourcesByKey.get(`${resourceType}:${resourceId}`)
    return findResourceOfType(resourceType, version, resourceId)(resource)
      ? resource
      : undefined
  }
}
